/**
 * CompleteProfilePage.js
 *
 * Gate de onboarding: se muestra después del primer login cuando el perfil
 * está incompleto. Lo decide el backend (backend/utils/perfilIncompleto.js)
 * y llega como user.perfil_incompleto; el gate está en App.js.
 *
 * (Lo de abajo es la nota de integración original, de antes de que la regla
 * pasara al backend.)
 * El usuario NO puede navegar al resto de la plataforma hasta completarlo.
 *
 * Uso:
 *  1. Agregar la ruta en App.js:
 *       <Route path="/completar-perfil" element={<CompleteProfilePage />} />
 *
 *  2. En AuthContext, después de GET /users/me, verificar si el perfil está
 *     incompleto y redirigir:
 *
 *       const isProfileIncomplete = (user) =>
 *         !user.first_name || !user.last_name || !user.username;
 *
 *       if (isProfileIncomplete(userData)) {
 *         navigate('/completar-perfil');
 *       }
 *
 *  3. En AuthContext / PrivateRoute, bloquear navegación si el perfil está
 *     incompleto:
 *
 *       // En PrivateRoute.js (o equivalente):
 *       if (user && isProfileIncomplete(user) && location.pathname !== '/completar-perfil') {
 *         return <Navigate to="/completar-perfil" replace />;
 *       }
 */

import React, { useState, useContext } from 'react';
import {
  Box, TextField, Typography, Button, AppBar, Toolbar,
  Alert, LinearProgress, InputAdornment, IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import logo from '../assets/images/logo.png';
import api from '../api/api';
import { AuthContext } from '../context/AuthContext';

// ─── helpers ────────────────────────────────────────────────────────────────

const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

const passwordStrength = (pwd) => {
  let score = 0;
  if (pwd.length >= 8)       score++;
  if (/[A-Z]/.test(pwd))     score++;
  if (/[a-z]/.test(pwd))     score++;
  if (/\d/.test(pwd))        score++;
  if (/[\W_]/.test(pwd))     score++;
  return score;
};
const strengthLabel = (s) => ['', 'Muy débil', 'Débil', 'Regular', 'Buena', 'Fuerte'][s] ?? '';
const strengthColor = (s) => ['', 'error', 'error', 'warning', 'info', 'success'][s] ?? 'error';

// ─── componente ─────────────────────────────────────────────────────────────

const CompleteProfilePage = () => {
  const navigate        = useNavigate();
  const { user, setUser } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState(false);

  const [showPass,    setShowPass]    = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // ── Aceptación legal para prestadores ────────────────────────────────────
  const isPrestador = user?.role === 'prestador';
  const [legalAccepted, setLegalAccepted]         = useState(false); // Guías de la Comunidad
  const [habilitacionesAccepted, setHabilitacionesAccepted] = useState(false); // Seguros y habilitaciones

  const [formData, setFormData] = useState({
    first_name:      user?.first_name    || '',
    last_name:       user?.last_name     || '',
    username:        user?.username      || '',
    phone:           user?.phone         || '',
    street:          user?.street        || '',
    city:            user?.city          || '',
    province:        user?.province      || '',
    postal_code:     user?.postal_code   || '',
    dni:             user?.dni           || '',
    birthdate:       user?.birthdate     ? user.birthdate.slice(0, 10) : '',
    password:        '',
    confirmPassword: '',
  });

  const set = (field) => (e) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    setError('');

    // validaciones
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setError('El nombre y apellido son obligatorios.');
      return;
    }
    if (!formData.username.trim()) {
      setError('El nombre de usuario es obligatorio.');
      return;
    }
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(formData.username)) {
      setError('El nombre de usuario debe tener entre 3 y 20 caracteres (letras, números y _).');
      return;
    }
    if (!formData.phone) {
      setError('El teléfono es obligatorio.');
      return;
    }
    if (!formData.dni.trim()) {
      setError('El DNI / CUIT es obligatorio.');
      return;
    }
    if (!formData.birthdate) {
      setError('La fecha de nacimiento es obligatoria.');
      return;
    }

    // contraseña: solo si el usuario quiso cambiarla
    if (formData.password || formData.confirmPassword) {
      if (!passwordRegex.test(formData.password)) {
        setError('La contraseña debe tener al menos 8 caracteres, mayúsculas, minúsculas, números y símbolos.');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Las contraseñas no coinciden.');
        return;
      }
    }

    // validación legal exclusiva para prestadores
    if (isPrestador) {
      if (!legalAccepted) {
        setError('Debés aceptar las Guías de la Comunidad para prestadores antes de continuar.');
        return;
      }
      if (!habilitacionesAccepted) {
        setError('Debés confirmar que contás con los seguros y habilitaciones requeridos.');
        return;
      }
    }

    setLoading(true);
    try {
      await api.put('/users/me', {
        first_name:  formData.first_name.trim(),
        last_name:   formData.last_name.trim(),
        username:    formData.username.trim(),
        phone:       formData.phone,
        street:      formData.street.trim()      || undefined,
        city:        formData.city.trim()        || undefined,
        province:    formData.province.trim()    || undefined,
        postal_code: formData.postal_code.trim() || undefined,
        dni:         formData.dni.trim(),
        birthdate:   formData.birthdate,
      });

      // cambio de contraseña (opcional)
      if (formData.password) {
        // Si el usuario no tenía contraseña (caso edge) usamos change-password
        // Si viene del flujo normal de registro ya tiene contraseña seteada
        await api.put('/users/change-password', {
          currentPassword: formData.password, // no aplica en este gate — ver nota abajo
          newPassword:     formData.password,
        });
      }

      setSuccess(true);

      // El user fresco va al contexto: trae perfil_incompleto recalculado por
      // el backend. Antes se pedía y se descartaba, y el gate seguía viendo
      // el user viejo (incompleto) y podía devolver acá.
      const { data } = await api.get('/users/me');
      setUser(data.user || data);
      setTimeout(() => navigate('/'), 1200);
    } catch (err) {
      if (err.response?.status === 409) {
        setError('El nombre de usuario ya está en uso. Elegí otro.');
      } else {
        setError(err.response?.data?.error || 'No se pudo guardar el perfil. Intentá de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── estilos compartidos ───────────────────────────────────────────────────

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': { borderColor: '#163244' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#163244' },
  };

  const sectionTitle = (text, optional = false) => (
    <Typography
      variant="subtitle2"
      fontWeight={700}
      color="#163244"
      fontFamily="Montserrat, sans-serif"
      mt={3}
      mb={1}
    >
      {text}{' '}
      {optional && (
        <Typography component="span" variant="caption" color="text.secondary">
          (opcional)
        </Typography>
      )}
    </Typography>
  );

  const strength = passwordStrength(formData.password);

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f0f2f5', fontFamily: 'Montserrat, sans-serif' }}>
      {/* AppBar */}
      <AppBar position="static" sx={{ backgroundColor: '#163244', boxShadow: 'none' }}>
        <Toolbar>
          <Box sx={{ cursor: 'default', display: 'flex', alignItems: 'center' }}>
            <img src={logo} alt="Fixo Logo" height={40} />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Banner explicativo */}
      <Box sx={{ backgroundColor: '#163244', color: '#fff', textAlign: 'center', py: 2, px: 2 }}>
        <Typography variant="body2" fontFamily="Montserrat, sans-serif" fontWeight={500}>
          Para continuar usá Fixo necesitás completar tu perfil. Solo lo hacés una vez. 🙌
        </Typography>
      </Box>

      {/* Card */}
      <Box
        sx={{
          maxWidth: 520,
          mx: 'auto',
          mt: 4,
          mb: 6,
          p: { xs: 3, sm: 4 },
          backgroundColor: '#fff',
          borderRadius: 3,
          boxShadow: '0 4px 24px rgba(22,50,68,0.10)',
        }}
      >
        <Typography
          variant="h6"
          fontWeight={700}
          fontFamily="Montserrat, sans-serif"
          color="#163244"
          mb={0.5}
        >
          Completá tu perfil
        </Typography>
        <Typography variant="body2" color="text.secondary" fontFamily="Montserrat, sans-serif" mb={2}>
          Los campos marcados con * son obligatorios.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, fontFamily: 'Montserrat, sans-serif' }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2, fontFamily: 'Montserrat, sans-serif' }}>
            ¡Perfil guardado! Redirigiendo…
          </Alert>
        )}

        {/* — Datos personales — */}
        {sectionTitle('Datos personales')}

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <TextField
            label="Nombre *"
            value={formData.first_name}
            onChange={set('first_name')}
            size="small"
            sx={inputSx}
          />
          <TextField
            label="Apellido *"
            value={formData.last_name}
            onChange={set('last_name')}
            size="small"
            sx={inputSx}
          />
        </Box>

        <TextField
          fullWidth
          label="Nombre de usuario *"
          value={formData.username}
          onChange={set('username')}
          margin="normal"
          size="small"
          helperText="3-20 caracteres. Solo letras, números y _"
          sx={inputSx}
        />

        <Box mt={1} mb={0.5}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontFamily: 'Montserrat, sans-serif' }}>
            Teléfono *
          </Typography>
          <PhoneInput
            country="ar"
            value={formData.phone}
            onChange={(phone) => setFormData((p) => ({ ...p, phone }))}
            inputStyle={{
              width: '100%',
              height: 40,
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 14,
              borderColor: '#c4c4c4',
              borderRadius: 4,
            }}
            buttonStyle={{ borderColor: '#c4c4c4', borderRadius: '4px 0 0 4px' }}
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1.5 }}>
          <TextField
            label="DNI / CUIT *"
            value={formData.dni}
            onChange={set('dni')}
            size="small"
            sx={inputSx}
          />
          <TextField
            type="date"
            label="Fecha de nacimiento *"
            InputLabelProps={{ shrink: true }}
            value={formData.birthdate}
            onChange={set('birthdate')}
            size="small"
            sx={inputSx}
          />
        </Box>

        {/* — Domicilio — */}
        {sectionTitle('Domicilio', true)}

        <TextField
          fullWidth
          label="Calle y número"
          value={formData.street}
          onChange={set('street')}
          margin="dense"
          size="small"
          sx={inputSx}
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
          <TextField
            label="Localidad"
            value={formData.city}
            onChange={set('city')}
            size="small"
            sx={inputSx}
          />
          <TextField
            label="Provincia"
            value={formData.province}
            onChange={set('province')}
            size="small"
            sx={inputSx}
          />
        </Box>
        <TextField
          label="Código postal"
          value={formData.postal_code}
          onChange={set('postal_code')}
          margin="dense"
          size="small"
          sx={{ ...inputSx, width: '50%' }}
        />

        {/* — Contraseña (opcional en este gate) — */}
        {sectionTitle('Cambiar contraseña', true)}
        <Typography variant="caption" color="text.secondary" fontFamily="Montserrat, sans-serif">
          Completá solo si querés cambiarla ahora.
        </Typography>

        <TextField
          fullWidth
          label="Nueva contraseña"
          type={showPass ? 'text' : 'password'}
          value={formData.password}
          onChange={set('password')}
          margin="dense"
          size="small"
          sx={inputSx}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPass((v) => !v)}>
                  {showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {formData.password && (
          <Box mt={0.5} mb={1}>
            <LinearProgress
              variant="determinate"
              value={(strength / 5) * 100}
              color={strengthColor(strength)}
              sx={{ height: 4, borderRadius: 2 }}
            />
            <Typography variant="caption" color={`${strengthColor(strength)}.main`} fontFamily="Montserrat, sans-serif">
              {strengthLabel(strength)}
            </Typography>
          </Box>
        )}

        <TextField
          fullWidth
          label="Confirmar nueva contraseña"
          type={showConfirm ? 'text' : 'password'}
          value={formData.confirmPassword}
          onChange={set('confirmPassword')}
          margin="dense"
          size="small"
          sx={inputSx}
          error={!!formData.confirmPassword && formData.password !== formData.confirmPassword}
          helperText={
            formData.confirmPassword && formData.password !== formData.confirmPassword
              ? 'Las contraseñas no coinciden'
              : ''
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowConfirm((v) => !v)}>
                  {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* — Aceptación legal para prestadores — */}
        {isPrestador && (
          <>
            {sectionTitle('Condiciones para prestadores')}
            <Typography variant="caption" color="text.secondary" fontFamily="Montserrat, sans-serif" sx={{ display: 'block', mb: 1.5 }}>
              Como prestador de servicios en hogares, necesitamos tu confirmación explícita.
            </Typography>

            {/* Checkbox 1 — Guías de la Comunidad */}
            <Box
              onClick={() => setLegalAccepted(v => !v)}
              sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1.5,
                p: 1.5, mb: 1.5, borderRadius: 2, cursor: 'pointer',
                border: legalAccepted
                  ? '1px solid rgba(22,50,68,0.2)'
                  : '1px solid rgba(231,164,50,0.4)',
                background: legalAccepted ? 'rgba(22,50,68,0.03)' : 'rgba(231,164,50,0.04)',
                transition: 'all 0.2s',
              }}
            >
              <Box sx={{
                width: 18, height: 18, borderRadius: 0.5, flexShrink: 0, mt: 0.2,
                border: `2px solid ${legalAccepted ? '#163244' : '#E7A432'}`,
                background: legalAccepted ? '#163244' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
              }}>
                {legalAccepted && (
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, color: '#2c3e50', lineHeight: 1.6, userSelect: 'none' }}
                onClick={e => e.stopPropagation()}
              >
                He leído y acepto las{' '}
                <Link
                  to="/comunidad"
                  target="_blank"
                  style={{ color: '#163244', fontWeight: 700, textDecoration: 'underline' }}
                >
                  Guías de la Comunidad
                </Link>
                {' '}y los{' '}
                <Link
                  to="/terminos"
                  target="_blank"
                  style={{ color: '#163244', fontWeight: 700, textDecoration: 'underline' }}
                >
                  Términos y Condiciones
                </Link>
                {' '}de FIXO para prestadores, incluyendo las normas de comportamiento en hogares de clientes. *
              </Typography>
            </Box>

            {/* Checkbox 2 — Habilitaciones y seguros */}
            <Box
              onClick={() => setHabilitacionesAccepted(v => !v)}
              sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1.5,
                p: 1.5, mb: 1, borderRadius: 2, cursor: 'pointer',
                border: habilitacionesAccepted
                  ? '1px solid rgba(22,50,68,0.2)'
                  : '1px solid rgba(231,164,50,0.4)',
                background: habilitacionesAccepted ? 'rgba(22,50,68,0.03)' : 'rgba(231,164,50,0.04)',
                transition: 'all 0.2s',
              }}
            >
              <Box sx={{
                width: 18, height: 18, borderRadius: 0.5, flexShrink: 0, mt: 0.2,
                border: `2px solid ${habilitacionesAccepted ? '#163244' : '#E7A432'}`,
                background: habilitacionesAccepted ? '#163244' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
              }}>
                {habilitacionesAccepted && (
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, color: '#2c3e50', lineHeight: 1.6, userSelect: 'none' }}
                onClick={e => e.stopPropagation()}
              >
                Declaro que cuento con los <strong>seguros, habilitaciones y capacitaciones</strong> necesarias
                para realizar los servicios que voy a ofrecer, y que soy responsable de mantenerlos vigentes. *
              </Typography>
            </Box>

            {/* Info box */}
            <Box sx={{
              background: '#EAF0F4',
              border: '1px solid #b8cdd9',
              borderLeft: '4px solid #163244',
              borderRadius: 2, p: 1.5, mb: 1,
            }}>
              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: 11.5, color: '#2c3e50', lineHeight: 1.65 }}>
                <strong>Importante:</strong> FIXO no es responsable por los servicios que prestás.
                Como prestador independiente, sos responsable de tu trabajo, tu seguridad
                y la seguridad de los clientes.
              </Typography>
            </Box>
          </>
        )}

        {/* CTA */}
        <Button
          variant="contained"
          fullWidth
          sx={{
            mt: 4,
            py: 1.4,
            backgroundColor: '#E7A432',
            color: '#163244',
            fontWeight: 700,
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 15,
            opacity: (isPrestador && (!legalAccepted || !habilitacionesAccepted)) ? 0.55 : 1,
            '&:hover': { backgroundColor: '#cf9228' },
            '&:disabled': { opacity: 0.6 },
          }}
          onClick={handleSave}
          disabled={loading || success}
        >
          {loading ? 'Guardando…' : 'Guardar y continuar'}
        </Button>
      </Box>
    </Box>
  );
};

export default CompleteProfilePage;
